import Service, { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import ENV from 'lumen/config/environment';
import type { CentralMappingBreakdown } from 'lumen/models';
import {
  BREAKDOWNS,
  PMS_VALUE_TYPES,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';

/**
 * TEMPORARY mock for the channel breakdown (epic PIN-1206 — Net revenue channel
 * mapping). The channel entitlement and its `central-mapping/breakdown` record
 * are not yet served by the API, so this service fakes them client-side so the
 * Channels card renders (with its value-type tabs) without the backend.
 * Everything is gated behind the `net-revenue-settings` toggle.
 *
 * This is a single, self-contained file: to remove it once the channel API is
 * live, delete this service and its `channelBreakdownMock` uses (grep
 * `channelBreakdownMock` / `isChannelMockEnabled`). The `net-revenue-settings`
 * toggle and all generic channel wiring stay.
 */

const MOCK_CHANNEL_VALUE_TYPES: PmsValueType[] = [
  PMS_VALUE_TYPES.TRAVEL_AGENT,
  PMS_VALUE_TYPES.SOURCE,
  PMS_VALUE_TYPES.ORIGIN,
];

export default class ChannelBreakdownMockService extends Service {
  @service declare featureToggle: Services['feature-toggle'];
  @service declare store: Services['store'];

  @cached
  get breakdown(): CentralMappingBreakdown {
    const valueTypes = MOCK_CHANNEL_VALUE_TYPES.map((valueType) => {
      this.store.push({
        data: {
          type: 'central-mapping/pms-value-type',
          id: `mock-channel-vt-${valueType}`,
          attributes: { valueType, available: true },
        },
      });
      return {
        type: 'central-mapping/pms-value-type',
        id: `mock-channel-vt-${valueType}`,
      };
    });

    return this.store.push({
      data: {
        type: 'central-mapping/breakdown',
        id: 'mock-channel-breakdown',
        attributes: {
          breakdown: BREAKDOWNS.CHANNEL,
          available: true,
          totalValuesUnmapped: 0,
        },
        relationships: {
          valueTypes: { data: valueTypes },
        },
      },
    });
  }

  get isChannelMockEnabled(): boolean {
    return (
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.NET_REVENUE_SETTINGS,
      ) && ENV.environment !== 'test'
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'channel-breakdown-mock': ChannelBreakdownMockService;
  }
}
